"use client"

import React, { useEffect, useRef } from "react"
import { rovingRadioGroup } from "@/app/utils/roving-radio-group"

export interface ChoiceChipOption<T extends string> {
    value: T
    label: string
}

interface ChoiceChipsProps<T extends string> {
    options: ChoiceChipOption<T>[]
    value: T
    onChange: (value: T) => void
    ariaLabelledBy?: string
    testIdPrefix?: string
    // A bleeding row (-mx + px) also needs a matching scroll-px, or snap-start scrolls the first chip under the padding
    className?: string
}

const CHIP_CLASS =
    "h-9 shrink-0 cursor-pointer snap-start whitespace-nowrap rounded-full px-4 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue"

export default function ChoiceChips<T extends string>({
    options,
    value,
    onChange,
    ariaLabelledBy,
    testIdPrefix,
    className = "",
}: ChoiceChipsProps<T>) {
    const rowRef = useRef<HTMLDivElement>(null)
    const radioGroup = rovingRadioGroup(options.map((option) => option.value), value, onChange)

    // Scrolls the row, not the page, to the selected chip, again whenever the row turns visible or is resized
    useEffect(() => {
        const row = rowRef.current
        if (!row) {
            return
        }
        const revealSelectedChip = () => {
            const chip = row.querySelector<HTMLElement>('[aria-checked="true"]')
            if (chip && chip.offsetLeft + chip.offsetWidth > row.clientWidth) {
                row.scrollLeft = chip.offsetLeft - parseFloat(getComputedStyle(row).paddingLeft)
            }
        }
        const observer = new ResizeObserver(revealSelectedChip)
        observer.observe(row)
        return () => observer.disconnect()
    }, [value])

    return (
        <div
            ref={rowRef}
            role="radiogroup"
            aria-labelledby={ariaLabelledBy}
            onKeyDown={radioGroup.onKeyDown}
            data-test={testIdPrefix ? `${testIdPrefix}-chips` : undefined}
            className={`relative flex snap-x gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
        >
            {options.map((option) => {
                const isSelected = option.value === value
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={radioGroup.tabIndexFor(option.value)}
                        data-test={testIdPrefix ? `${testIdPrefix}-chip-${option.value}` : undefined}
                        onClick={() => onChange(option.value)}
                        className={`${CHIP_CLASS} ${
                            isSelected
                                ? "bg-rm-blue font-bold text-white"
                                : "bg-white text-pale shadow-[inset_0_0_0_1px_#E5E7EB] hover:shadow-[inset_0_0_0_1px_#9AA0AC]"
                        }`}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}
